export { TableModel, SortingOrder, ColumnType };
/** 
 * Модель таблицы.
 * @author Алексей Васьковский <avaskovskiy@umney.ru>
 */
class TableModel {
  /**
   * Столбцы таблицы.
   * @type {TableModel~Column[]}
   * @see {@link TableModel~Column}
   */
  columns = [];
  /**
   * Строки таблицы.
   * @type {Object[]}
   */
  rows = [];
  /**
   * Модель сортировки.
   * @type {TableModel~SortingModel[]}
   * @default []
   * @readonly
   * @see {@link TableModel~SortingModel}
   */
  sortModel = [];
  /**
   * Максимальная высота таблицы.
   * 
   * Задаётся строкой в единицах CSS, например, "20rem" или "100px".
   * 
   * @type {string|undefined}
   */
  maxHeight;
  /**
   * Копирует свойства объекта.
   * @param {TableModel} props - Свойства объекта.
   */
  constructor(props) {
    Object.assign(this, props);
  }
  /**
   * Сортирует таблицу.
   * @param {TableModel~Column} column - 
   * Столбец, по которому сортируется таблица.
   * @param {SortingOrder} [sort=column.sortingOrder] - Порядок сортировки.
   */
  sort(column, sort) {
    if (sort) column.sortingOrder = sort;
    else sort = column.sortingOrder ?? SortingOrder.ASC;
    this.sortModel = [{ field: column.field, sort }];
    console.debug("TableModel.sort", column.field, sort);
    this.rows = this.rows.sort(
      (a, b) => (sort == SortingOrder.DESC ? -1 : 1)
        * this.compare(column.type, a[column.field], b[column.field]));
  }
  /**
   * Переключает порядок сортировки по столбцу.
   * @param {TableModel~Column} column - 
   * Столбец по которому сортируется таблица.
   */
  toggleSort(column) {
    const model = this.sortModel.length ? this.sortModel[0] : null;
    const sort = model && column.field == model.field ?
      (SortingOrder.ASC == model.sort ?
        SortingOrder.DESC :
        SortingOrder.ASC) :
      SortingOrder.ASC;
    this.sort(column, sort);
  }
  compare(type, a, b) {
    switch (type) {
      case "number": return a - b;
      case "string": return String(a).localeCompare(b);
      case "file": return this.compare("string", a.name, b.name);
      case "link": return this.compare("string", a.name, b.name);
      case "boolean":
      case "date":
      case "dateTime":
        return a < b ? -1 : Number(a > b);
      default:
        console.error("TableModel.compare:", "disparate", a, b);
        return a < b ? -1 : Number(a > b);
    }
  }
}
/**
 * Порядок сортировки.
 * @enum {string}
 * @see [MUI:DataGrid](https://mui.com/x/api/data-grid/data-grid/#data-grid-prop-sortModel)
 */
const SortingOrder =
{
  /** По возрастанию */
  ASC: "asc",
  /** По убыванию */
  DESC: "desc"
};
/**
 * Тип столбца.
 * @enum {string}
 * @see [MUI:ColumnType](https://mui.com/x/react-data-grid/column-definition/#column-types)
 */
const ColumnType =
{
  /** 
   * По умолчанию.
   */
  STRING: "string",
  /** 
   * Отображается иконкой с выравниванием по центру. 
   */
  BOOLEAN: "boolean",
  /**
   * Отображается с локализацией (запятая вместо точки) 
   * с выравниванием по правому краю.
   */
  NUMBER: "number",
  /**
   * Отображается только дата с выравниванием по правому краю.
   */
  DATE: "date",
  /**
   * Отображается дата / время с выравниванием по правому краю.
   */
  DATE_TIME: "dateTime",
  /**
   * Ссылка в формате `{ name, href, target, download, onClick }`.
   */
  LINK: "link",
  /**
   * Изображение в формате `{ name, src, href, target, download, onClick }`.
   */
  IMG: "img",
  /**
   * Файл в формате
   * `{ name, mime_type, preview, href, target, download, onClick }`.
   */
  FILE: "file",
  /**
   * Произвольный HTML код.
   */
  HTML: "html",
  /**
   * Произвольный компонент JSX.
   */
  JSX: "jsx",
};
/**
 * Столбец таблицы.
 * @typedef {Object} TableModel~Column
 * @property {string} field - Идентификатор поля.
 * @property {ColumnType} type - Тип данных.
 * @property {string} headerName - Заголовок.
 * @property {boolean} [hide=false] - Скрыть столбец.
 * @property {boolean} [sortable=false] - 
 * Возможность сортировки по данному полю.
 * @property {SortingOrder} [sortingOrder=SortingOrder.ASC] -
 * Порядок сортировки.
 * @see [MUI:GridColDef](https://mui.com/x/react-data-grid/column-definition/)
 */
/**
 * Модель сортировки.
 * @typedef {Object} TableModel~SortingModel
 * @property {string} field - Название поля.
 * @property {SortingOrder} [sort=SortingOrder.ASC] - Порядок сортировки.
 * @see [MUI:DataGrid](https://mui.com/x/api/data-grid/data-grid/#data-grid-prop-sortModel)
 */
